import { useCallback, useEffect, useState } from "react";
import { fetchHomepageContent, saveHomepageContent } from "../api/homepageApi";

// Loads the homepage content for editing and holds a working copy. `content` is
// the editable draft; `media`/`library` hold pending file uploads until save.
export function useHomepageEditor() {
  const [content, setContent] = useState(null);
  const [serverMedia, setServerMedia] = useState({});
  const [serverLibrary, setServerLibrary] = useState({});
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Pending uploads not yet saved.
  const [pendingMedia, setPendingMedia] = useState({}); // { attr: File }
  const [pendingLibrary, setPendingLibrary] = useState({}); // { key: File }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchHomepageContent();
      setContent(data.content);
      setServerMedia(data.media || {});
      setServerLibrary(data.library || {});
      setSections(data.sections || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Merge a patch into the draft content (deep at the top level).
  const patch = useCallback((updater) => {
    setContent((prev) => (typeof updater === "function" ? updater(prev) : { ...prev, ...updater }));
  }, []);

  const stageMedia = useCallback((attr, file) => {
    setPendingMedia((m) => ({ ...m, [attr]: file }));
  }, []);

  const stageLibrary = useCallback((key, file) => {
    setPendingLibrary((m) => ({ ...m, [key]: file }));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const library = Object.entries(pendingLibrary).map(([key, file]) => ({ key, file }));
      const data = await saveHomepageContent(content, { media: pendingMedia, library });
      setContent(data.content);
      setServerMedia(data.media || {});
      setServerLibrary(data.library || {});
      setPendingMedia({});
      setPendingLibrary({});
      return data;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setSaving(false);
    }
  }, [content, pendingMedia, pendingLibrary]);

  // Resolve the display URL for a keyed library image (pending upload preview
  // takes precedence over the saved one).
  const libraryUrl = useCallback(
    (key) => {
      if (!key) return null;
      if (pendingLibrary[key]) return URL.createObjectURL(pendingLibrary[key]);
      return serverLibrary[key] || null;
    },
    [pendingLibrary, serverLibrary],
  );

  const mediaUrl = useCallback(
    (attr) => {
      if (pendingMedia[attr]) return URL.createObjectURL(pendingMedia[attr]);
      return serverMedia[attr] || null;
    },
    [pendingMedia, serverMedia],
  );

  return {
    content, sections, loading, saving, error,
    patch, stageMedia, stageLibrary, save, reload: load,
    libraryUrl, mediaUrl,
  };
}
